import { z } from 'zod';
import type { TIndexedFees } from '../../components/types';
import type { Merge } from 'simplytyped';

export const buildFeeTermSchema = (prefix = 'fees conditions terms ') => {
  return z
    .object({
      min: z.coerce
        .number({
          required_error: `${prefix}min is required`,
          invalid_type_error: `${prefix}min must be a number`
        })
        .step(0.01, `${prefix}min must be a multiple of 0.01`)
        .nonnegative(),
      max: z.coerce
        .number({
          required_error: `${prefix}max is required`,
          invalid_type_error: `${prefix}max must be a number`
        })
        .step(0.01, `${prefix}max must be a multiple of 0.01`)
        .refine((max) => max > 0 || max === -1, {
          message: `${prefix}max must be greater than zero, or negative one (unlimited)`
        }),
      unit: z.string(), // appears to be only month for now? @TODO
      // unit: z.enum(['month']), // appears to be only month for now?
      amount: z.coerce
        .number({
          required_error: `${prefix}amount is required`,
          invalid_type_error: `${prefix}amount must be a number`
        })
        .step(0.01, `${prefix}amount must be a multiple of 0.01`)
        .nonnegative(),
      gst: z.coerce
        .number({
          required_error: `${prefix}gst is required`,
          invalid_type_error: `${prefix}gst must be a number`
        })
        .step(0.01, `${prefix}gst must be a multiple of 0.01`)
        .nonnegative()
        .optional(),
      loanAmounts: z
        .array(
          z
            .object({
              min: z.coerce
                .number({
                  required_error: `${prefix}loanAmounts min is required`,
                  invalid_type_error: `${prefix}loanAmounts min must be a number`
                })
                .step(
                  0.01,
                  `${prefix}loanAmounts min must be a multiple of 0.01`
                )
                .nonnegative(),
              max: z.coerce
                .number({
                  required_error: `${prefix}loanAmounts max is required`,
                  invalid_type_error: `${prefix}loanAmounts max must be a number`
                })
                .step(
                  0.01,
                  `${prefix}loanAmounts max must be a multiple of 0.01`
                )
                .refine((max) => max > 0 || max === -1, {
                  message: `${prefix}loanAmounts max must be greater than zero, or negative one (unlimited)`
                }),
              amount: z.coerce
                .number({
                  required_error: `${prefix}loanAmounts amount is required`,
                  invalid_type_error: `${prefix}loanAmounts amount must be a number`
                })
                .step(
                  0.01,
                  `${prefix}loanAmounts amount must be a multiple of 0.01`
                )
                .nonnegative(),
              gst: z.coerce
                .number({
                  required_error: `${prefix}loanAmounts gst is required`,
                  invalid_type_error: `${prefix}loanAmounts gst must be a number`
                })
                .step(0.01)
                .nonnegative()
                .optional(),
              frequencies: z
                .object({
                  amount: z.coerce
                    .number({
                      required_error: `${prefix}loanAmounts amount is required`,
                      invalid_type_error: `${prefix}loanAmounts amount must be a number`
                    })
                    .step(
                      0.01,
                      `${prefix}loanAmounts amount must be a multiple of 0.01`
                    )
                    .nonnegative(),
                  frequency: z.enum(['Weekly', 'Fortnightly', 'Monthly'], {
                    required_error: `${prefix}loanAmounts frequency is required`,
                    invalid_type_error: `${prefix}loanAmounts frequency must be a string`
                  })
                })
                .array()
                // frequencies are optional and are typically missing
                .optional()
            })
            .refine((data) => data.max === -1 || data.max >= data.min, {
              message: `${prefix}loanAmounts max must be greater than or equal to min`,
              path: ['max']
            })
        )
        .default([])
    })
    .refine(({ max = 0, min = 0 }) => max === -1 || max > min, {
      message: `Term Range maximum must be greater than the minimum`,
      path: ['max']
    });
};

const buildFeeDataSchema = (prefix = 'fees ') =>
  z.object({
    id: z.string({
      required_error: `${prefix}id is required`,
      invalid_type_error: `${prefix}id must be a string`
    }),
    name: z.string({
      required_error: `${prefix}name is required`,
      invalid_type_error: `${prefix}name must be a string`
    }),
    state: z.string({
      required_error: `${prefix}state is required`,
      invalid_type_error: `${prefix}state must be a string`
    }),
    required: z
      .boolean({
        // required_error: `${prefix}required is required`,
        invalid_type_error: `${prefix}required must be a boolean`
      })
      .default(false),
    amount: z.coerce
      .number({
        required_error: `${prefix}amount is required`,
        invalid_type_error: `${prefix}amount must be a number`
      })
      .step(0.01, `${prefix}amount must be a multiple of 0.01`)
      .nonnegative(),
    gst: z.coerce
      .number({
        required_error: `${prefix}gst is required`,
        invalid_type_error: `${prefix}gst must be a number`
      })
      .step(0.01, `${prefix}gst must be a multiple of 0.01`)
      .nonnegative()
      .optional(),
    type: z.string({
      required_error: `${prefix}type is required`,
      invalid_type_error: `${prefix}type must be a string`
    }),
    range: z
      .object({
        min: z.coerce
          .number({
            required_error: `${prefix}range min is required`,
            invalid_type_error: `${prefix}range min must be a number`
          })
          .step(0.01, `${prefix}range min must be a multiple of 0.01`),
        max: z.coerce
          .number({
            required_error: `${prefix}range max is required`,
            invalid_type_error: `${prefix}range max must be a number`
          })
          .step(0.01, `${prefix}range max must be a multiple of 0.01`)
      })
      .refine((data) => data.max >= data.min, {
        message: `${prefix}range max must be greater than or equal to min`,
        path: ['max']
      })
      .optional(),
    glCode: z
      .string({
        required_error: `${prefix}glCode is required`,
        invalid_type_error: `${prefix}glCode must be a string`
      })
      .optional(),
    daysInArrears: z.coerce
      .number({
        required_error: `${prefix}daysInArrears is required`,
        invalid_type_error: `${prefix}daysInArrears must be a number`
      })
      .int()
      .nonnegative()
      .optional(),
    productFeeKey: z
      .string({
        required_error: `${prefix}productFeeKey is required`,
        invalid_type_error: `${prefix}productFeeKey must be a string`
      })
      .optional(),
    feeAmountByFrequency: z
      .object({
        weekly: z.coerce
          .number({
            required_error: `${prefix}feeAmountByFrequency weekly is required`,
            invalid_type_error: `${prefix}feeAmountByFrequency weekly must be a number`
          })
          .step(
            0.01,
            `${prefix}feeAmountByFrequency weekly must be a multiple of 0.01`
          )
          .nonnegative(),
        fortnightly: z.coerce
          .number({
            required_error: `${prefix}feeAmountByFrequency fortnightly is required`,
            invalid_type_error: `${prefix}feeAmountByFrequency fortnightly must be a number`
          })
          .step(
            0.01,
            `${prefix}feeAmountByFrequency fortnightly must be a multiple of 0.01`
          )
          .nonnegative(),
        monthly: z.coerce
          .number({
            required_error: `${prefix}feeAmountByFrequency monthly is required`,
            invalid_type_error: `${prefix}feeAmountByFrequency monthly must be a number`
          })
          .step(
            0.01,
            `${prefix}feeAmountByFrequency monthly must be a multiple of 0.01`
          )
          .nonnegative()
      })
      .optional(),
    amortized: z
      .union([z.boolean(), z.string()])
      .refine(
        (value) =>
          value === 'true' || value === 'false' || typeof value === 'boolean',
        {
          message: `${prefix}amortized must be a boolean or the string "true" or "false"`
        }
      )
      .transform((value) => {
        if (value === 'true') return true;
        if (value === 'false') return false;
        return value;
      })
      .optional(),
    feePaidBy: z
      .string({
        required_error: `${prefix}feePaidBy is required`,
        invalid_type_error: `${prefix}feePaidBy must be a string`
      })
      .optional(),
    externalPaymentSourceId: z
      .string({
        required_error: `${prefix}externalPaymentSourceId is required`,
        invalid_type_error: `${prefix}externalPaymentSourceId must be a string`
      })
      .optional(),
    systemCreationMethod: z
      .string({
        required_error: `${prefix}systemCreationMethod is required`,
        invalid_type_error: `${prefix}systemCreationMethod must be a string`
      })
      .optional(),
    conditions: z
      .object({
        terms: buildFeeTermSchema(`${prefix} conditions terms `)
          .array()
          .default([])
      })
      .optional()
  });

export const ProductFeeTermSchema = buildFeeTermSchema();
export const ProductFeeDataSchema = buildFeeDataSchema();
export type TProductFeeData = Merge<
  ReturnType<typeof ProductFeeDataSchema.parse>,
  {
    feeMatrix?: TIndexedFees;
  }
>;

export const editTermsFormFields = z
  .object({
    terms: z.array(ProductFeeTermSchema).default([])
  })
  .strict();

export type EditTermsSchema = z.infer<typeof editTermsFormFields>;
export type ProductFeeTermType = z.infer<typeof ProductFeeTermSchema>;

export const CustomFeesSchema = z
  .array(
    z
      .array(
        z.union([
          z.object({
            contentType: z.literal('number'),
            content: z.coerce.number().nonnegative()
          }),
          z.object({
            contentType: z.literal('percent'),
            content: z.coerce.number().nonnegative()
          }),
          z.object({
            contentType: z.literal('percent*100'),
            content: z.coerce.number().nonnegative()
          }),
          z.object({
            contentType: z.literal('text'),
            content: z.string()
          }),
          z.object({
            contentType: z.literal('currency'),
            content: z.coerce.number().nonnegative()
          }),
          z.object({
            content: z.object({
              min: z.number().nonnegative().default(0),
              max: z
                .number()
                .refine((n) => n >= 0 || n === -1, {
                  message: 'Number must be non-negative or -1'
                })
                .default(0)
            }),
            contentType: z.literal('range')
          })
        ])
      )
      .default([])
  )
  .default([]);
export type CustomFeesType = z.infer<typeof CustomFeesSchema>;

export const RefinedTermSchema = z
  .array(ProductFeeTermSchema)
  .default([])
  .refine(
    (data) => {
      for (let i = 1; i < data.length; i++) {
        const prev = data[i - 1];
        const current = data[i];

        if (prev.max !== current.min && prev.max + 0.01 !== current.min) {
          return false;
        }
      }
      return true;
    },
    {
      message:
        'The maximum of the preceding range should equal the minimum of the next range'
    }
  )
  .refine(
    (data) => {
      for (let i = 0; i < data.length - 1; i++) {
        if (data[i].max === -1) {
          return false;
        }
      }

      return true;
    },
    {
      message: 'Only the last term can have a maximum of -1'
    }
  )
  .refine(
    (data) => {
      if (data.length > 0) {
        return data[0].min === 0;
      }

      return true;
    },
    {
      message: 'The first term range must have a minimum of 0'
    }
  );
